import { avatarSrc } from "@/lib/defaults";
import { pausableInterval } from "@/lib/appVisibility";
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { sharedGet } from "@/lib/sharedGet";
import { useApp } from "@/context/AppContext";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { MessageCircle } from "lucide-react";
import { useIsMobile } from "@/lib/useIsMobile";
import { dmDraftKey, getChatDraft, draftPreview } from "@/lib/chatDrafts";

// Header chat launcher: unread badge + dropdown of conversations (photo + name +
// last message). Clicking a conversation opens the floating ChatDock directly.
export const HeaderChatButton = () => {
  const { user, t, openChat } = useApp();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [convos, setConvos] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadConvos = useCallback(() => {
    api.get("/chat/conversations").then(({ data }) => setConvos(data.conversations || [])).catch(() => {});
  }, []);

  useEffect(() => { if (open) loadConvos(); }, [open, loadConvos]);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const load = (force = false) => sharedGet("/me/nav-badges", { ttl: 20000, force }).then((data) => { if (alive) setUnread(data.messages_unread || 0); }).catch(() => {});
    load();
    const stop = pausableInterval(() => load(false), 25000); // lot 699 — repos en arrière-plan
    const on = () => load(true);
    window.addEventListener("yalola:chat", on);
    window.addEventListener("yalola:read", on);
    return () => { alive = false; stop(); window.removeEventListener("yalola:chat", on); window.removeEventListener("yalola:read", on); };
  }, [user, loadConvos]);

  if (!user) return null;

  // Mobile / tablet: the icon opens the full-screen Messages page (no dropdown).
  if (isMobile) {
    return (
      <Link to="/messages" className="tab-icon relative" aria-label={t("nav.messages", "Discussions")} data-testid="header-chat-btn">
        <MessageCircle size={20} />
        {unread > 0 && (
          <span
            className="absolute top-1.5 right-2 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white grid place-items-center"
            style={{ background: "var(--yalola-primary)" }}
            data-testid="header-chat-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </Link>
    );
  }

  const pick = (other) => { setOpen(false); openChat(other); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="tab-icon relative" aria-label={t("nav.messages", "Discussions")} data-testid="header-chat-btn">
          <MessageCircle size={20} />
          {unread > 0 && (
            <span
              className="absolute top-1.5 right-2 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white grid place-items-center"
              style={{ background: "var(--yalola-primary)" }}
              data-testid="header-chat-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="header-chat-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="font-bold font-display">{t("nav.messages", "Discussions")}</div>
          <Link
            to="/messages"
            onClick={() => setOpen(false)}
            className="text-xs font-semibold"
            style={{ color: "var(--yalola-primary)" }}
            data-testid="header-chat-see-all"
          >
            {t("chat.see_all", "Tout voir")}
          </Link>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {convos.length === 0 && (
            <div className="p-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="header-chat-empty">
              {t("chat.empty", "Aucune conversation pour l'instant. Ouvre le profil d'un membre pour discuter.")}
            </div>
          )}
          {convos.map((c) => (
            <button
              key={c.id}
              onClick={() => pick(c.other)}
              className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-[color:var(--yalola-surface-alt)] transition-colors"
              data-testid={`header-chat-conv-${c.id}`}
            >
              <Avatar className="h-10 w-10 shrink-0"><AvatarImage src={avatarSrc(c.other)} /><AvatarFallback>{c.other?.name?.[0]}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate flex items-center gap-1">{c.other?.name || "?"} <VerifiedBadge user={c.other} size={12} /></div>
                <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>
                  {draftPreview(getChatDraft(dmDraftKey(c.other?.id)))
                    ? <><span className="font-semibold" style={{ color: "var(--yalola-primary)" }}>{t("chat.draft_label", "Brouillon :")}</span> {draftPreview(getChatDraft(dmDraftKey(c.other?.id)))}</>
                    : (c.last_message || "—")}
                </div>
              </div>
              {c.unread > 0 && (
                <span
                  className="min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-bold text-white shrink-0"
                  style={{ background: "var(--yalola-primary)" }}
                  data-testid={`header-chat-conv-badge-${c.id}`}
                >
                  {c.unread > 9 ? "9+" : c.unread}
                </span>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default HeaderChatButton;
